import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useBackgroundAudio from '../hooks/useBackgroundAudio';

/**
 * WEIGH Interactive - A contemplative tile-based experience
 * 
 * Words appear as tiles that can be tapped to acknowledge.
 * Tapped words dim and remain. Untapped words fade away.
 * Three waves of words, separated by reflective pauses.
 */

// Word groups for each wave
const WAVE_1 = ['time', 'energy', 'attention', 'access', 'silence', 'availability'];
const WAVE_2 = ['tolerance', 'endurance', 'appeasement', 'overextension', 'justification', 'performance'];
const WAVE_3 = ['dignity', 'focus', 'rest', 'integrity', 'autonomy'];

const WeighInteractive = ({ onExit }) => {
  const [phase, setPhase] = useState(0);
  // Phase 0: Opening microcopy
  // Phase 1: Wave 1 tiles
  // Phase 2: Wave 2 tiles
  // Phase 3: Accumulation line
  // Phase 4: Wave 3 tiles
  // Phase 5: Final microcopy
  // Phase 6: Exit options

  const [visibleTiles, setVisibleTiles] = useState([]);
  const [tappedTiles, setTappedTiles] = useState([]); // Array to maintain order for stacking
  const [showMicrocopy, setShowMicrocopy] = useState(null);
  const [showExitOptions, setShowExitOptions] = useState(false);
  const [fadingTiles, setFadingTiles] = useState(new Set());
  
  const tileIdRef = useRef(0);
  const activeTimersRef = useRef([]);
  const spawnIntervalRef = useRef(null);

  // Subtle background audio
  useBackgroundAudio(true);

  // Clean up all timers on unmount
  useEffect(() => {
    return () => {
      activeTimersRef.current.forEach(t => clearTimeout(t));
      if (spawnIntervalRef.current) {
        clearInterval(spawnIntervalRef.current);
      }
    };
  }, []);

  // Generate a unique tile with positioning in upper screen, avoiding overlaps
  const createTile = useCallback((word, wave) => {
    tileIdRef.current += 1;
    
    const index = tileIdRef.current - 1;
    
    // Create a grid layout strictly on left and right sides, upper portion only
    // Left side: 5-30%, Right side: 70-95%, Upper 60% of screen
    const gridPositions = [
      // Left side positions - column 1 (x: 8-15%)
      { x: 10, y: 10 },
      { x: 12, y: 25 },
      { x: 8, y: 40 },
      { x: 14, y: 55 },
      // Left side positions - column 2 (x: 22-30%)
      { x: 25, y: 12 },
      { x: 28, y: 28 },
      { x: 23, y: 42 },
      { x: 27, y: 55 },
      // Right side positions - column 1 (x: 70-78%)
      { x: 73, y: 10 },
      { x: 75, y: 25 },
      { x: 72, y: 40 },
      { x: 76, y: 55 },
      // Right side positions - column 2 (x: 85-92%)
      { x: 88, y: 12 },
      { x: 90, y: 28 },
      { x: 86, y: 42 },
      { x: 89, y: 55 },
      // Extra positions for overflow
      { x: 15, y: 18 },
      { x: 85, y: 18 },
    ];
    
    // Use modulo to cycle through positions, add slight randomness
    const posIndex = index % gridPositions.length;
    const basePos = gridPositions[posIndex];
    
    // Add small random offset to avoid perfect grid feel
    const offsetX = (Math.random() - 0.5) * 4;
    const offsetY = (Math.random() - 0.5) * 4;
    
    return {
      id: tileIdRef.current,
      word,
      wave,
      x: Math.max(5, Math.min(95, basePos.x + offsetX)),
      y: Math.max(8, Math.min(58, basePos.y + offsetY)), // Keep in upper 58% of screen
    };
  }, []);

  // Spawn tiles for a wave - one at a time
  const spawnWave = useCallback((words, wave, onComplete) => {
    // Clear any existing interval
    if (spawnIntervalRef.current) {
      clearInterval(spawnIntervalRef.current);
    }
    
    let index = 0;
    
    // Spawn first tile immediately
    if (words.length > 0) {
      const tile = createTile(words[0], wave);
      setVisibleTiles(prev => [...prev, tile]);
      index = 1;
    }
    
    // Spawn remaining tiles at intervals
    if (words.length > 1) {
      spawnIntervalRef.current = setInterval(() => {
        if (index < words.length) {
          const tile = createTile(words[index], wave);
          setVisibleTiles(prev => [...prev, tile]);
          index++;
        }
        
        if (index >= words.length) {
          clearInterval(spawnIntervalRef.current);
          spawnIntervalRef.current = null;
          if (onComplete) {
            const timer = setTimeout(onComplete, 3000); // Pause after wave completes
            activeTimersRef.current.push(timer);
          }
        }
      }, 1200); // One tile every 1.2 seconds
    } else if (onComplete) {
      const timer = setTimeout(onComplete, 3000);
      activeTimersRef.current.push(timer);
    }
  }, [createTile]);

  // Phase 0: Opening microcopy
  useEffect(() => {
    if (phase === 0) {
      const showTimer = setTimeout(() => {
        setShowMicrocopy('opening');
      }, 1000);
      
      const hideTimer = setTimeout(() => {
        setShowMicrocopy(null);
        setPhase(1);
      }, 4000);
      
      activeTimersRef.current.push(showTimer, hideTimer);
    }
  }, [phase]);

  // Phase 1: Wave 1 tiles
  useEffect(() => {
    if (phase === 1) {
      const startTimer = setTimeout(() => {
        spawnWave(WAVE_1, 1, () => setPhase(2));
      }, 500);
      activeTimersRef.current.push(startTimer);
    }
  }, [phase, spawnWave]);

  // Phase 2: Wave 2 tiles
  useEffect(() => {
    if (phase === 2) {
      const startTimer = setTimeout(() => {
        spawnWave(WAVE_2, 2, () => setPhase(3));
      }, 500);
      activeTimersRef.current.push(startTimer);
    }
  }, [phase, spawnWave]);

  // Phase 3: Accumulation line
  useEffect(() => {
    if (phase === 3) {
      const showTimer = setTimeout(() => {
        setShowMicrocopy('accumulation');
      }, 500);
      
      const hideTimer = setTimeout(() => {
        setShowMicrocopy(null);
        setPhase(4);
      }, 4500);
      
      activeTimersRef.current.push(showTimer, hideTimer);
    }
  }, [phase]);

  // Phase 4: Wave 3 tiles
  useEffect(() => {
    if (phase === 4) {
      const startTimer = setTimeout(() => {
        spawnWave(WAVE_3, 3, () => {
          // After wave 3, mark untapped tiles for fading
          setVisibleTiles(prev => {
            const tappedIds = new Set(tappedTiles.map(t => t.id));
            const untappedIds = prev.filter(t => !tappedIds.has(t.id)).map(t => t.id);
            setFadingTiles(new Set(untappedIds));
            return prev;
          });
          setPhase(5);
        });
      }, 500);
      activeTimersRef.current.push(startTimer);
    }
  }, [phase, spawnWave, tappedTiles]);

  // Phase 5: Final microcopy
  useEffect(() => {
    if (phase === 5) {
      const showTimer = setTimeout(() => {
        setShowMicrocopy('final');
      }, 2000);
      
      const hideTimer = setTimeout(() => {
        setShowMicrocopy(null);
        setPhase(6);
      }, 6000);
      
      activeTimersRef.current.push(showTimer, hideTimer);
    }
  }, [phase]);

  // Phase 6: Show exit options
  useEffect(() => {
    if (phase === 6) {
      const showTimer = setTimeout(() => {
        setShowExitOptions(true);
      }, 1000);
      activeTimersRef.current.push(showTimer);
    }
  }, [phase]);

  // Maximum stack size
  const MAX_STACK_SIZE = 10;
  
  // Check if stack is full
  const isStackFull = tappedTiles.length >= MAX_STACK_SIZE;

  // Handle tile tap - add to stack at bottom
  const handleTileTap = useCallback((tile) => {
    const tappedIds = new Set(tappedTiles.map(t => t.id));
    if (tappedIds.has(tile.id)) return;
    if (tappedTiles.length >= MAX_STACK_SIZE) return; // Stack full
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(5);
    }
    
    const newTappedTiles = [...tappedTiles, tile];
    setTappedTiles(newTappedTiles);
    
    // If this was the 10th tap, fade all remaining tiles
    if (newTappedTiles.length >= MAX_STACK_SIZE) {
      setVisibleTiles(prev => {
        const tappedIds = new Set(newTappedTiles.map(t => t.id));
        const untappedIds = prev.filter(t => !tappedIds.has(t.id)).map(t => t.id);
        setFadingTiles(new Set(untappedIds));
        return prev;
      });
      
      // Show exit options after a pause
      const timer = setTimeout(() => {
        setShowExitOptions(true);
      }, 3000);
      activeTimersRef.current.push(timer);
    }
  }, [tappedTiles]);

  // Handle exit selection
  const handleExit = useCallback((choice) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    onExit(choice);
  }, [onExit]);

  // Check if tile is tapped
  const isTileTapped = useCallback((tileId) => {
    return tappedTiles.some(t => t.id === tileId);
  }, [tappedTiles]);

  // Get stack position for a tapped tile (index in stack)
  const getStackIndex = useCallback((tileId) => {
    return tappedTiles.findIndex(t => t.id === tileId);
  }, [tappedTiles]);

  // Get tile opacity based on state
  const getTileOpacity = (tile) => {
    const tapped = isTileTapped(tile.id);
    if (fadingTiles.has(tile.id) && !tapped) {
      return 0; // Fading away (untapped)
    }
    if (tapped) {
      return 0.55; // Visible in stack
    }
    return 0.7; // Normal visibility
  };

  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden"
      style={{ backgroundColor: 'hsl(240, 8%, 3%)' }}
    >
      {/* Opening Microcopy: "What does holding this require?" */}
      <AnimatePresence>
        {showMicrocopy === 'opening' && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-lg sm:text-xl text-center px-6"
            style={{ color: 'hsl(40, 10%, 55%)' }}
          >
            What does holding this require?
          </motion.p>
        )}
      </AnimatePresence>

      {/* Tiles */}
      <AnimatePresence>
        {visibleTiles.map(tile => {
          const tapped = isTileTapped(tile.id);
          const stackIndex = tapped ? getStackIndex(tile.id) : -1;
          
          // Calculate stack position at bottom
          // Stack grows upward from bottom
          const stackY = tapped ? `calc(85% - ${stackIndex * 28}px)` : `${tile.y}%`;
          const stackX = tapped ? '50%' : `${tile.x}%`;
          
          return (
            <motion.button
              key={tile.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ 
                opacity: getTileOpacity(tile),
                scale: tapped ? 0.95 : 1,
                left: stackX,
                top: stackY,
                x: '-50%',
                y: tapped ? '0%' : '-50%'
              }}
              exit={{ opacity: 0 }}
              transition={{ 
                duration: fadingTiles.has(tile.id) ? 3 : (tapped ? 0.8 : 1.5),
                ease: tapped ? [0.25, 0.1, 0.25, 1] : 'easeOut'
              }}
              onClick={() => handleTileTap(tile)}
              disabled={tapped}
              className="absolute font-display text-base sm:text-lg tracking-wide cursor-pointer px-4 py-2 transition-colors duration-500 disabled:cursor-default"
              style={{
                color: tapped 
                  ? 'hsl(40, 10%, 48%)' 
                  : 'hsl(40, 12%, 55%)',
                border: 'none',
                background: 'transparent'
              }}
            >
              {tile.word}
            </motion.button>
          );
        })}
      </AnimatePresence>

      {/* Accumulation Line */}
      <AnimatePresence>
        {showMicrocopy === 'accumulation' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-4"
          >
            {/* Horizontal line */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 120 }}
              transition={{ duration: 2, ease: 'easeOut' }}
              className="h-px"
              style={{ backgroundColor: 'hsl(40, 10%, 25%)' }}
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.45 }}
              transition={{ duration: 1.5, delay: 1 }}
              className="font-display text-sm sm:text-base italic text-center"
              style={{ color: 'hsl(40, 8%, 48%)' }}
            >
              Some weight accumulates.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Final Microcopy: "Stay with what is present." */}
      <AnimatePresence>
        {showMicrocopy === 'final' && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.45 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute bottom-1/3 left-1/2 -translate-x-1/2 font-display text-base sm:text-lg italic text-center"
            style={{ color: 'hsl(40, 10%, 50%)' }}
          >
            Stay with what is present.
          </motion.p>
        )}
      </AnimatePresence>

      {/* Exit options */}
      <AnimatePresence>
        {showExitOptions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2.5 }}
            className="absolute bottom-12 left-0 right-0 flex justify-center gap-10 sm:gap-14 md:gap-18"
          >
            {['Feel', 'Translate', 'Close'].map((option, index) => (
              <motion.button
                key={option}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.3, duration: 1.5 }}
                onClick={() => handleExit(option.toLowerCase())}
                className="font-display text-base sm:text-lg tracking-[0.12em] transition-all duration-700 hover:tracking-[0.18em]"
                style={{ color: 'hsl(40, 10%, 52%)' }}
              >
                {option}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WeighInteractive;
